<script setup lang="ts">
interface Props {
  name: string;
  size?: number;
  strokeWidth?: number;
}

withDefaults(defineProps<Props>(), {
  size: 20,
  strokeWidth: 2,
});
</script>

<template>
  <svg
    :width="size"
    :height="size"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    :stroke-width="strokeWidth"
    stroke-linecap="round"
    stroke-linejoin="round"
  >
    <!-- Palette icon -->
    <template v-if="name === 'palette'">
      <circle cx="13.5" cy="6.5" r=".5" />
      <circle cx="17.5" cy="10.5" r=".5" />
      <circle cx="8.5" cy="7.5" r=".5" />
      <circle cx="6.5" cy="12.5" r=".5" />
      <path
        d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.926 0 1.648-.746 1.648-1.688 0-.437-.18-.835-.437-1.125-.29-.289-.438-.652-.438-1.125a1.64 1.64 0 0 1 1.668-1.668h1.996c3.051 0 5.555-2.503 5.555-5.554C21.965 6.012 17.461 2 12 2z"
      />
    </template>

    <!-- User icon -->
    <template v-else-if="name === 'user' || name === 'User'">
      <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </template>

    <!-- File-text icon -->
    <template v-else-if="name === 'file-text'">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <polyline points="10 9 9 9 8 9" />
    </template>

    <!-- Bold icon -->
    <template v-else-if="name === 'bold'">
      <path d="M6 4h8a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
      <path d="M6 12h9a4 4 0 0 1 4 4 4 4 0 0 1-4 4H6z" />
    </template>

    <!-- Italic icon -->
    <template v-else-if="name === 'italic'">
      <line x1="19" x2="10" y1="4" y2="4" />
      <line x1="14" x2="5" y1="20" y2="20" />
      <line x1="15" x2="9" y1="4" y2="20" />
    </template>

    <!-- List (bullets) icon -->
    <template v-else-if="name === 'list'">
      <line x1="8" x2="21" y1="6" y2="6" />
      <line x1="8" x2="21" y1="12" y2="12" />
      <line x1="8" x2="21" y1="18" y2="18" />
      <line x1="3" x2="3.01" y1="6" y2="6" />
      <line x1="3" x2="3.01" y1="12" y2="12" />
      <line x1="3" x2="3.01" y1="18" y2="18" />
    </template>

    <!-- ClipboardList (Standards nav) -->
    <template v-else-if="name === 'clipboard-list' || name === 'ClipboardList'">
      <rect width="8" height="4" x="8" y="2" rx="1" ry="1" />
      <path
        d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"
      />
      <path d="M12 11h4" />
      <path d="M12 16h4" />
      <path d="M8 11h.01" />
      <path d="M8 16h.01" />
    </template>

    <!-- Link icon -->
    <template v-else-if="name === 'link'">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </template>

    <!-- Building-2 icon -->
    <template v-else-if="name === 'building-2' || name === 'Building2'">
      <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
      <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
      <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
      <path d="M10 6h4" />
      <path d="M10 10h4" />
      <path d="M10 14h4" />
      <path d="M10 18h4" />
    </template>

    <!-- Chevrons left (collapse) -->
    <template v-else-if="name === 'chevrons-left'">
      <polyline points="11 17 6 12 11 7" />
      <polyline points="18 17 13 12 18 7" />
    </template>

    <!-- Chevrons right (expand) -->
    <template v-else-if="name === 'chevrons-right'">
      <polyline points="7 7 12 12 7 17" />
      <polyline points="14 7 19 12 14 17" />
    </template>

    <!-- Menu -->
    <template v-else-if="name === 'menu'">
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </template>

    <!-- Log out -->
    <template v-else-if="name === 'log-out'">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </template>

    <!-- Slack -->
    <template v-else-if="name === 'slack'">
      <rect
        x="13"
        y="2"
        width="3"
        height="8"
        rx="1.5"
        stroke="#2EB67D"
        fill="#2EB67D"
      />
      <path
        d="M19 8.5V10h1.5A1.5 1.5 0 1 0 19 8.5"
        stroke="#2EB67D"
        fill="#2EB67D"
      />
      <rect
        x="8"
        y="14"
        width="3"
        height="8"
        rx="1.5"
        stroke="#E01E5A"
        fill="#E01E5A"
      />
      <path
        d="M5 15.5V14H3.5A1.5 1.5 0 1 0 5 15.5"
        stroke="#E01E5A"
        fill="#E01E5A"
      />
      <rect
        x="14"
        y="13"
        width="8"
        height="3"
        rx="1.5"
        stroke="#ECB22E"
        fill="#ECB22E"
      />
      <path
        d="M15.5 19H14v1.5a1.5 1.5 0 1 0 1.5-1.5"
        stroke="#ECB22E"
        fill="#ECB22E"
      />
      <rect
        x="2"
        y="8"
        width="8"
        height="3"
        rx="1.5"
        stroke="#36C5F0"
        fill="#36C5F0"
      />
      <path
        d="M8.5 5H10V3.5A1.5 1.5 0 1 0 8.5 5"
        stroke="#36C5F0"
        fill="#36C5F0"
      />
    </template>

    <!-- Sun -->
    <template v-else-if="name === 'sun'">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2" />
      <path d="M12 20v2" />
      <path d="m4.93 4.93 1.41 1.41" />
      <path d="m17.66 17.66 1.41 1.41" />
      <path d="M2 12h2" />
      <path d="M20 12h2" />
      <path d="m6.34 17.66-1.41 1.41" />
      <path d="m19.07 4.93-1.41 1.41" />
    </template>

    <!-- Moon -->
    <template v-else-if="name === 'moon'">
      <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />
    </template>

    <!-- Message Circle -->
    <template v-else-if="name === 'message-circle'">
      <path
        d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"
      />
    </template>

    <!-- X -->
    <template v-else-if="name === 'x' || name === 'X'">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </template>

    <!-- Target -->
    <template v-else-if="name === 'target'">
      <circle cx="12" cy="12" r="10" />
      <circle cx="12" cy="12" r="6" />
      <circle cx="12" cy="12" r="2" />
    </template>

    <!-- Flag -->
    <template v-else-if="name === 'flag'">
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
      <line x1="4" y1="22" x2="4" y2="15" />
    </template>

    <!-- Layers -->
    <template v-else-if="name === 'layers'">
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </template>

    <!-- Book Open -->
    <template v-else-if="name === 'book-open'">
      <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
      <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
    </template>

    <!-- User Plus -->
    <template v-else-if="name === 'user-plus'">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="20" y1="8" x2="20" y2="14" />
      <line x1="23" y1="11" x2="17" y2="11" />
    </template>

    <!-- Log In -->
    <template v-else-if="name === 'log-in'">
      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
      <polyline points="10 17 15 12 10 7" />
      <line x1="15" y1="12" x2="3" y2="12" />
    </template>

    <!-- Plug -->
    <template v-else-if="name === 'plug'">
      <path d="M12 22v-5" />
      <path d="M9 8V2" />
      <path d="M15 8V2" />
      <path d="M18 8v5a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4V8Z" />
    </template>

    <!-- Heart Pulse -->
    <template v-else-if="name === 'heart-pulse'">
      <path
        d="M2 9.5a5.5 5.5 0 0 1 9.591-3.676.56.56 0 0 0 .818 0A5.49 5.49 0 0 1 22 9.5c0 2.29-1.5 4-3 5.5l-5.492 5.313a2 2 0 0 1-3 .019L5 15c-1.5-1.5-3-3.2-3-5.5"
      />
      <path d="M3.22 13H9.5l.5-1 2 4.5 2-7 1.5 3.5h5.27" />
    </template>

    <!-- Calendar -->
    <template v-else-if="name === 'calendar' || name === 'Calendar'">
      <path d="M8 2v4" />
      <path d="M16 2v4" />
      <rect width="18" height="18" x="3" y="4" rx="2" />
      <path d="M3 10h18" />
    </template>

    <!-- Settings -->
    <template v-else-if="name === 'settings' || name === 'Settings'">
      <path
        d="M12.22 2h-.44a2 2 0 0 0-2 2v.18a2 2 0 0 1-1 1.73l-.43.25a2 2 0 0 1-2 0l-.15-.08a2 2 0 0 0-2.73.73l-.22.38a2 2 0 0 0 .73 2.73l.15.1a2 2 0 0 1 1 1.72v.51a2 2 0 0 1-1 1.74l-.15.09a2 2 0 0 0-.73 2.73l.22.38a2 2 0 0 0 2.73.73l.15-.08a2 2 0 0 1 2 0l.43.25a2 2 0 0 1 1 1.73V20a2 2 0 0 0 2 2h.44a2 2 0 0 0 2-2v-.18a2 2 0 0 1 1-1.73l.43-.25a2 2 0 0 1 2 0l.15.08a2 2 0 0 0 2.73-.73l.22-.39a2 2 0 0 0-.73-2.73l-.15-.08a2 2 0 0 1-1-1.74v-.5a2 2 0 0 1 1-1.74l.15-.09a2 2 0 0 0 .73-2.73l-.22-.38a2 2 0 0 0-2.73-.73l-.15.08a2 2 0 0 1-2 0l-.43-.25a2 2 0 0 1-1-1.73V4a2 2 0 0 0-2-2z"
      />
      <circle cx="12" cy="12" r="3" />
    </template>

    <!-- Check -->
    <template v-else-if="name === 'check' || name === 'Check'">
      <path d="M20 6 9 17l-5-5" />
    </template>

    <!-- Sparkles -->
    <template v-else-if="name === 'sparkles' || name === 'Sparkles'">
      <path
        d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"
      />
      <path d="M20 3v4" />
      <path d="M22 5h-4" />
      <path d="M4 17v2" />
      <path d="M5 18H3" />
    </template>

    <!-- Loader2 -->
    <template v-else-if="name === 'loader-2' || name === 'Loader2'">
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </template>

    <!-- Info -->
    <template v-else-if="name === 'info' || name === 'Info'">
      <circle cx="12" cy="12" r="10" />
      <path d="M12 16v-4" />
      <path d="M12 8h.01" />
    </template>

    <!-- AlertTriangle -->
    <template v-else-if="name === 'alert-triangle' || name === 'AlertTriangle'">
      <path
        d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"
      />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </template>

    <!-- LayoutDashboard -->
    <template v-else-if="name === 'layout-dashboard'">
      <rect width="7" height="9" x="3" y="3" rx="1" />
      <rect width="7" height="5" x="14" y="3" rx="1" />
      <rect width="7" height="9" x="14" y="12" rx="1" />
      <rect width="7" height="5" x="3" y="16" rx="1" />
    </template>

    <!-- MessageSquare -->
    <template v-else-if="name === 'MessageSquare'">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </template>

    <!-- ThumbsUp -->
    <template v-else-if="name === 'ThumbsUp'">
      <path d="M7 10v12" />
      <path
        d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"
      />
    </template>

    <!-- Lightbulb -->
    <template v-else-if="name === 'lightbulb' || name === 'Lightbulb'">
      <path
        d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5"
      />
      <path d="M9 18h6" />
      <path d="M10 22h4" />
    </template>

    <!-- Activity -->
    <template v-else-if="name === 'activity' || name === 'Activity'">
      <path
        d="M22 12h-2.48a2 2 0 0 0-1.93 1.46l-2.35 8.36a.25.25 0 0 1-.48 0L9.24 2.18a.25.25 0 0 0-.48 0l-2.35 8.36A2 2 0 0 1 4.49 12H2"
      />
    </template>

    <!-- TrendingUp -->
    <template v-else-if="name === 'TrendingUp'">
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </template>

    <!-- TrendingDown -->
    <template v-else-if="name === 'TrendingDown'">
      <polyline points="22 17 13.5 8.5 8.5 13.5 2 7" />
      <polyline points="16 17 22 17 22 11" />
    </template>

    <!-- Minus -->
    <template v-else-if="name === 'Minus'">
      <path d="M5 12h14" />
    </template>

    <!-- AlertCircle -->
    <template v-else-if="name === 'AlertCircle'">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" x2="12" y1="8" y2="12" />
      <line x1="12" x2="12.01" y1="16" y2="16" />
    </template>

    <!-- RefreshCw -->
    <template v-else-if="name === 'RefreshCw' || name === 'refresh-cw'">
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M8 16H3v5" />
    </template>

    <!-- Users (Story 4.2: Team Dashboard) -->
    <template v-else-if="name === 'users' || name === 'Users'">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </template>

    <!-- Bell (Story 4.2: Attention Alerts) -->
    <template v-else-if="name === 'Bell'">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </template>

    <!-- PieChart (Story 4.2: Feedback Distribution) -->
    <template v-else-if="name === 'PieChart'">
      <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
      <path d="M22 12A10 10 0 0 0 12 2v10z" />
    </template>

    <!-- Tags (Story 4.2: Team Themes) -->
    <template v-else-if="name === 'Tags'">
      <path d="m15 5 6.3 6.3a2.4 2.4 0 0 1 0 3.4L17 19" />
      <path
        d="M9.586 5.586A2 2 0 0 0 8.172 5H3a1 1 0 0 0-1 1v5.172a2 2 0 0 0 .586 1.414L8.29 18.29a2.426 2.426 0 0 0 3.42 0l3.58-3.58a2.426 2.426 0 0 0 0-3.42z"
      />
      <circle cx="6.5" cy="9.5" r=".5" fill="currentColor" />
    </template>

    <!-- ChevronRight (Story 4.2: Navigation) -->
    <template v-else-if="name === 'ChevronRight' || name === 'chevron-right'">
      <path d="m9 18 6-6-6-6" />
    </template>

    <!-- CheckCircle (Story 4.2: Empty states) -->
    <template v-else-if="name === 'CheckCircle' || name === 'check-circle'">
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </template>

    <!-- CheckCircle2 (Story 4.6: Action rate icon) -->
    <template v-else-if="name === 'CheckCircle2'">
      <circle cx="12" cy="12" r="10" />
      <path d="m9 12 2 2 4-4" />
    </template>

    <!-- MessageSquareOff (Story 4.2: Feedback gap alert) -->
    <template v-else-if="name === 'MessageSquareOff'">
      <path d="M21 15V5a2 2 0 0 0-2-2H9" />
      <path d="m2 2 20 20" />
      <path d="M3.6 3.6c-.4.3-.6.8-.6 1.4v16l4-4h10" />
    </template>

    <!-- Frown (Story 4.2: Sentiment decline alert) -->
    <template v-else-if="name === 'Frown'">
      <circle cx="12" cy="12" r="10" />
      <path d="M16 16s-1.5-2-4-2-4 2-4 2" />
      <line x1="9" x2="9.01" y1="9" y2="9" />
      <line x1="15" x2="15.01" y1="9" y2="9" />
    </template>

    <!-- Award (Story 4.2: Recognition opportunity alert) -->
    <template v-else-if="name === 'Award'">
      <circle cx="12" cy="8" r="6" />
      <path d="M15.477 12.89 17 22l-5-3-5 3 1.523-9.11" />
    </template>

    <!-- ArrowLeft (Story 4.2: Back navigation) -->
    <template v-else-if="name === 'ArrowLeft' || name === 'arrow-left'">
      <path d="m12 19-7-7 7-7" />
      <path d="M19 12H5" />
    </template>

    <!-- MessageCircleWarning (Story 4.6: No feedback alert) -->
    <template v-else-if="name === 'MessageCircleWarning'">
      <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
      <path d="M12 8v4" />
      <path d="M12 16h.01" />
    </template>

    <!-- Download (Story 4.6: Export button) -->
    <template v-else-if="name === 'Download'">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" x2="12" y1="15" y2="3" />
    </template>

    <!-- Printer (Story 4.6: Print button) -->
    <template v-else-if="name === 'Printer'">
      <polyline points="6 9 6 2 18 2 18 9" />
      <path
        d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"
      />
      <rect width="12" height="8" x="6" y="14" />
    </template>

    <!-- Star (Story 4.6: Highlights, Activity: Recognitions) -->
    <template v-else-if="name === 'star' || name === 'Star'">
      <polygon
        points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"
      />
    </template>

    <!-- Workflow (Hierarchy page) -->
    <template v-else-if="name === 'Workflow'">
      <rect width="8" height="8" x="3" y="3" rx="2" />
      <path d="M7 11v4a2 2 0 0 0 2 2h4" />
      <rect width="8" height="8" x="13" y="13" rx="2" />
    </template>

    <!-- Search -->
    <template v-else-if="name === 'search' || name === 'Search'">
      <circle cx="11" cy="11" r="8" />
      <path d="m21 21-4.3-4.3" />
    </template>

    <!-- ChevronDown -->
    <template v-else-if="name === 'chevron-down' || name === 'ChevronDown'">
      <path d="m6 9 6 6 6-6" />
    </template>

    <!-- ChevronUp -->
    <template v-else-if="name === 'chevron-up' || name === 'ChevronUp'">
      <path d="m18 15-6-6-6 6" />
    </template>

    <!-- Plus -->
    <template v-else-if="name === 'Plus' || name === 'plus'">
      <path d="M5 12h14" />
      <path d="M12 5v14" />
    </template>

    <!-- Trash2 -->
    <template v-else-if="name === 'Trash2'">
      <path d="M3 6h18" />
      <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
      <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
      <line x1="10" x2="10" y1="11" y2="17" />
      <line x1="14" x2="14" y1="11" y2="17" />
    </template>

    <!-- Edit (Pencil) -->
    <template v-else-if="name === 'Edit' || name === 'Pencil'">
      <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
      <path d="m15 5 4 4" />
    </template>

    <!-- Upload -->
    <template v-else-if="name === 'Upload'">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" x2="12" y1="3" y2="15" />
    </template>

    <!-- UserMinus (Remove manager) -->
    <template v-else-if="name === 'UserMinus'">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <line x1="22" x2="16" y1="11" y2="11" />
    </template>

    <!-- LineChart (Story 4.3: Sentiment Trend) -->
    <template v-else-if="name === 'LineChart'">
      <path d="M3 3v16a2 2 0 0 0 2 2h16" />
      <path d="m19 9-5 5-4-4-3 3" />
    </template>

    <!-- Zap (Story 4.3: Emerging Themes) -->
    <template v-else-if="name === 'Zap'">
      <path
        d="M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z"
      />
    </template>

    <!-- Building (Story 4.3: Company Dashboard navigation) -->
    <template v-else-if="name === 'building'">
      <rect width="16" height="20" x="4" y="2" rx="2" ry="2" />
      <path d="M9 22v-4h6v4" />
      <path d="M8 6h.01" />
      <path d="M16 6h.01" />
      <path d="M12 6h.01" />
      <path d="M12 10h.01" />
      <path d="M12 14h.01" />
      <path d="M16 10h.01" />
      <path d="M16 14h.01" />
      <path d="M8 10h.01" />
      <path d="M8 14h.01" />
    </template>

    <!-- Wrench (Dev Tools) -->
    <template v-else-if="name === 'wrench'">
      <path
        d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
      />
    </template>

    <!-- Inbox (Activity: Feedback Received) -->
    <template v-else-if="name === 'inbox'">
      <polyline points="22 12 16 12 14 15 10 15 8 12 2 12" />
      <path
        d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"
      />
    </template>

    <!-- Send (Activity: Feedback Given) -->
    <template v-else-if="name === 'send'">
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </template>

    <!-- MessageSquarePlus (Activity: Feedback Requests) -->
    <template v-else-if="name === 'message-square-plus'">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      <path d="M12 7v6" />
      <path d="M9 10h6" />
    </template>

    <!-- Trophy (Activity: Achievements) -->
    <template v-else-if="name === 'trophy'">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M4 22h16" />
      <path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22" />
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
    </template>

    <!-- ArrowRight (Activity: Insights suggested action) -->
    <template v-else-if="name === 'arrow-right'">
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </template>

    <!-- TrendingUp (lowercase alias) -->
    <template v-else-if="name === 'trending-up'">
      <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
      <polyline points="16 7 22 7 22 13" />
    </template>

    <!-- Smile (Activity: Pulse positive sentiment) -->
    <template v-else-if="name === 'smile'">
      <circle cx="12" cy="12" r="10" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <line x1="9" x2="9.01" y1="9" y2="9" />
      <line x1="15" x2="15.01" y1="9" y2="9" />
    </template>

    <!-- Meh (Activity: Pulse neutral sentiment) -->
    <template v-else-if="name === 'meh'">
      <circle cx="12" cy="12" r="10" />
      <line x1="8" x2="16" y1="15" y2="15" />
      <line x1="9" x2="9.01" y1="9" y2="9" />
      <line x1="15" x2="15.01" y1="9" y2="9" />
    </template>

    <!-- BarChart3 (Weekly Digest) -->
    <template v-else-if="name === 'BarChart3' || name === 'bar-chart-3'">
      <path d="M3 3v16a2 2 0 0 0 2 2h16" />
      <path d="M7 16v-3" />
      <path d="M11 16v-8" />
      <path d="M15 16v-5" />
      <path d="M19 16v-10" />
    </template>

    <!-- Hash (Channel Analysis) -->
    <template v-else-if="name === 'hash' || name === 'Hash'">
      <line x1="4" x2="20" y1="9" y2="9" />
      <line x1="4" x2="20" y1="15" y2="15" />
      <line x1="10" x2="8" y1="3" y2="21" />
      <line x1="16" x2="14" y1="3" y2="21" />
    </template>

    <!-- Send (lowercase alias for digest send button) -->
    <template v-else-if="name === 'Send'">
      <path d="m22 2-7 20-4-9-9-4Z" />
      <path d="M22 2 11 13" />
    </template>

    <!-- NotebookPen (Activity: Manager Notes) -->
    <template v-else-if="name === 'notebook-pen'">
      <path
        d="M13.4 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7.4"
      />
      <path d="M2 6h4" />
      <path d="M2 10h4" />
      <path d="M2 14h4" />
      <path d="M2 18h4" />
      <path
        d="M21.174 6.812a1 1 0 0 0-3.986-3.987L11.5 8.513a1.5 1.5 0 0 0-.354.677l-.878 3.51a.5.5 0 0 0 .606.606l3.51-.878a1.5 1.5 0 0 0 .677-.354z"
      />
    </template>

    <!-- ShieldAlert (Activity: Employee page) -->
    <template v-else-if="name === 'shield-alert'">
      <path
        d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"
      />
      <path d="M12 8v4" />
      <path d="M12 16h.01" />
    </template>

    <!-- Compass (Org. Context nav) -->
    <template v-else-if="name === 'compass' || name === 'Compass'">
      <path
        d="m16.24 7.76-1.804 5.411a2 2 0 0 1-1.265 1.265L7.76 16.24l1.804-5.411a2 2 0 0 1 1.265-1.265z"
      />
      <circle cx="12" cy="12" r="10" />
    </template>

    <!-- CreditCard (Billing nav) -->
    <template v-else-if="name === 'credit-card'">
      <rect width="20" height="14" x="2" y="5" rx="2" />
      <line x1="2" x2="22" y1="10" y2="10" />
    </template>

    <!-- Copy icon -->
    <template v-else-if="name === 'copy' || name === 'Copy'">
      <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </template>

    <!-- Circle (fallback) -->
    <template v-else-if="name === 'circle'">
      <circle cx="12" cy="12" r="10" />
    </template>
  </svg>
</template>
